package com.mgacreative.hotel.shared.ui.designsystem.i18n

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

enum class AppLanguage(val code: String, val displayName: String, val flag: String) {
    EN("en", "English", "🇺🇸"),
    TR("tr", "Türkçe", "🇹🇷"),
    DE("de", "Deutsch", "🇩🇪"),
    FR("fr", "Français", "🇫🇷"),
    ES("es", "Español", "🇪🇸"),
    RU("ru", "Русский", "🇷🇺");

    companion object {
        fun fromCode(code: String): AppLanguage {
            val clean = code.lowercase().take(2)
            return entries.find { it.code == clean } ?: TR // Default to TR
        }
    }
}

object AppLocalizationManager {
    // Default language is TR
    private val _currentLanguage = MutableStateFlow(AppLanguage.TR)
    val currentLanguage: StateFlow<AppLanguage> = _currentLanguage.asStateFlow()

    init {
        // Auto detect system language
        detectAndSetSystemLanguage()
    }

    fun detectAndSetSystemLanguage(deviceLanguageCode: String? = null) {
        if (deviceLanguageCode != null) {
            _currentLanguage.value = AppLanguage.fromCode(deviceLanguageCode)
        }
    }

    fun setLanguage(language: AppLanguage) {
        _currentLanguage.value = language
    }

    private val translationDictionary: Map<String, Map<AppLanguage, String>> = mapOf(
        // Charts & Metrics
        "Haftalık Doluluk Oranı" to mapOf(
            AppLanguage.EN to "Weekly Occupancy Rate",
            AppLanguage.TR to "Haftalık Doluluk Oranı",
            AppLanguage.RU to "Еженедельная заполняемость",
            AppLanguage.DE to "Wöchentliche Auslastung",
            AppLanguage.FR to "Taux d'Occupation Hebdomadaire",
            AppLanguage.ES to "Tasa de Ocupación Semanal"
        ),
        "Aylık Doluluk Oranı" to mapOf(
            AppLanguage.EN to "Monthly Occupancy Rate",
            AppLanguage.TR to "Aylık Doluluk Oranı",
            AppLanguage.RU to "Ежемесячная заполняемость",
            AppLanguage.DE to "Monatliche Auslastung",
            AppLanguage.FR to "Taux d'Occupation Mensuel",
            AppLanguage.ES to "Tasa de Ocupación Mensual"
        ),
        "Yıllık Doluluk Oranı" to mapOf(
            AppLanguage.EN to "Yearly Occupancy Rate",
            AppLanguage.TR to "Yıllık Doluluk Oranı",
            AppLanguage.RU to "Годовая заполняемость",
            AppLanguage.DE to "Jährliche Auslastung",
            AppLanguage.FR to "Taux d'Occupation Annuel",
            AppLanguage.ES to "Tasa de Ocupación Anual"
        ),
        "Weekly Occupancy Rate" to mapOf(
            AppLanguage.EN to "Weekly Occupancy Rate",
            AppLanguage.TR to "Haftalık Doluluk Oranı",
            AppLanguage.RU to "Еженедельная заполняемость",
            AppLanguage.DE to "Wöchentliche Auslastung",
            AppLanguage.FR to "Taux d'Occupation Hebdomadaire",
            AppLanguage.ES to "Tasa de Ocupación Semanal"
        ),
        "Monthly Occupancy Rate" to mapOf(
            AppLanguage.EN to "Monthly Occupancy Rate",
            AppLanguage.TR to "Aylık Doluluk Oranı",
            AppLanguage.RU to "Ежемесячная заполняемость",
            AppLanguage.DE to "Monatliche Auslastung",
            AppLanguage.FR to "Taux d'Occupation Mensuel",
            AppLanguage.ES to "Tasa de Ocupación Mensual"
        ),
        "Yearly Occupancy Rate" to mapOf(
            AppLanguage.EN to "Yearly Occupancy Rate",
            AppLanguage.TR to "Yıllık Doluluk Oranı",
            AppLanguage.RU to "Годовая заполняемость",
            AppLanguage.DE to "Jährliche Auslastung",
            AppLanguage.FR to "Taux d'Occupation Annuel",
            AppLanguage.ES to "Tasa de Ocupación Anual"
        ),
        // Navigation & Menu Items
        "Gösterge Paneli" to mapOf(
            AppLanguage.EN to "Dashboard",
            AppLanguage.TR to "Gösterge Paneli",
            AppLanguage.RU to "Панель управления",
            AppLanguage.DE to "Dashboard",
            AppLanguage.FR to "Tableau de Bord",
            AppLanguage.ES to "Panel de Control"
        ),
        "Ön Büro (Rezervasyon)" to mapOf(
            AppLanguage.EN to "Front Desk (Reservations)",
            AppLanguage.TR to "Ön Büro (Rezervasyon)",
            AppLanguage.RU to "Стойка регистрации (Бронирование)",
            AppLanguage.DE to "Rezeption (Reservierungen)",
            AppLanguage.FR to "Réception (Réservations)",
            AppLanguage.ES to "Recepción (Reservas)"
        ),
        "Ön Büro" to mapOf(
            AppLanguage.EN to "Front Desk",
            AppLanguage.TR to "Ön Büro",
            AppLanguage.RU to "Стойка регистрации",
            AppLanguage.DE to "Rezeption",
            AppLanguage.FR to "Réception",
            AppLanguage.ES to "Recepción"
        ),
        "Oda Durumları" to mapOf(
            AppLanguage.EN to "Room Status",
            AppLanguage.TR to "Oda Durumları",
            AppLanguage.RU to "Статус номеров",
            AppLanguage.DE to "Zimmerstatus",
            AppLanguage.FR to "Statut des Chambres",
            AppLanguage.ES to "Estado de Habitaciones"
        ),
        "Kat Hizmetleri" to mapOf(
            AppLanguage.EN to "Housekeeping",
            AppLanguage.TR to "Kat Hizmetleri",
            AppLanguage.RU to "Уборка и обслуживание",
            AppLanguage.DE to "Reinigungsservice",
            AppLanguage.FR to "Service d'Étage",
            AppLanguage.ES to "Servicio de Limpieza"
        ),
        "Kasa & Finans" to mapOf(
            AppLanguage.EN to "Finance & Cash",
            AppLanguage.TR to "Kasa & Finans",
            AppLanguage.RU to "Финансы и касса",
            AppLanguage.DE to "Finanzen & Kasse",
            AppLanguage.FR to "Finance & Caisse",
            AppLanguage.ES to "Finanzas y Caja"
        ),
        "Kasa İşlemleri" to mapOf(
            AppLanguage.EN to "Cash Operations",
            AppLanguage.TR to "Kasa İşlemleri",
            AppLanguage.RU to "Кассовые операции",
            AppLanguage.DE to "Kassenvorgänge",
            AppLanguage.FR to "Opérations de Caisse",
            AppLanguage.ES to "Operaciones de Caja"
        ),
        "Personel Yönetimi" to mapOf(
            AppLanguage.EN to "Staff Management",
            AppLanguage.TR to "Personel Yönetimi",
            AppLanguage.RU to "Управление персоналом",
            AppLanguage.DE to "Personalverwaltung",
            AppLanguage.FR to "Gestion du Personnel",
            AppLanguage.ES to "Gestión de Personal"
        ),
        "Envanter" to mapOf(
            AppLanguage.EN to "Inventory",
            AppLanguage.TR to "Envanter",
            AppLanguage.RU to "Инвентарь",
            AppLanguage.DE to "Inventar",
            AppLanguage.FR to "Inventaire",
            AppLanguage.ES to "Inventario"
        ),
        "Envanter Yönetimi" to mapOf(
            AppLanguage.EN to "Inventory Management",
            AppLanguage.TR to "Envanter Yönetimi",
            AppLanguage.RU to "Управление инвентарем",
            AppLanguage.DE to "Bestandsverwaltung",
            AppLanguage.FR to "Gestion des Stocks",
            AppLanguage.ES to "Gestión de Inventario"
        ),
        "Sistem Yönetimi" to mapOf(
            AppLanguage.EN to "System Admin",
            AppLanguage.TR to "Sistem Yönetimi",
            AppLanguage.RU to "Системное администрирование",
            AppLanguage.DE to "Systemverwaltung",
            AppLanguage.FR to "Administration Système",
            AppLanguage.ES to "Administración del Sistema"
        ),
        "Yardım & Destek" to mapOf(
            AppLanguage.EN to "Help & Support",
            AppLanguage.TR to "Yardım & Destek",
            AppLanguage.RU to "Помощь и поддержка",
            AppLanguage.DE to "Hilfe & Support",
            AppLanguage.FR to "Aide & Support",
            AppLanguage.ES to "Ayuda y Soporte"
        ),
        "Oturumu Kapat" to mapOf(
            AppLanguage.EN to "Logout",
            AppLanguage.TR to "Oturumu Kapat",
            AppLanguage.RU to "Выйти из системы",
            AppLanguage.DE to "Abmelden",
            AppLanguage.FR to "Déconnexion",
            AppLanguage.ES to "Cerrar Sesión"
        ),
        "Dolu Odalar" to mapOf(
            AppLanguage.EN to "Occupied Rooms",
            AppLanguage.TR to "Dolu Odalar",
            AppLanguage.RU to "Занятые номера",
            AppLanguage.DE to "Belegte Zimmer",
            AppLanguage.FR to "Chambres Occupées",
            AppLanguage.ES to "Habitaciones Ocupadas"
        ),
        "Oda Özet" to mapOf(
            AppLanguage.EN to "Room Summary",
            AppLanguage.TR to "Oda Özet",
            AppLanguage.RU to "Сводка по номерам",
            AppLanguage.DE to "Zimmerübersicht",
            AppLanguage.FR to "Résumé des Chambres",
            AppLanguage.ES to "Resumen de Habitaciones"
        ),
        "Vardiya Planı" to mapOf(
            AppLanguage.EN to "Shift Planner",
            AppLanguage.TR to "Vardiya Planı",
            AppLanguage.RU to "График смен",
            AppLanguage.DE to "Schichtplaner",
            AppLanguage.FR to "Planificateur d'Équipe",
            AppLanguage.ES to "Planificador de Turnos"
        ),
        "Personel Listesi" to mapOf(
            AppLanguage.EN to "Staff List",
            AppLanguage.TR to "Personel Listesi",
            AppLanguage.RU to "Список сотрудников",
            AppLanguage.DE to "Personalliste",
            AppLanguage.FR to "Liste du Personnel",
            AppLanguage.ES to "Lista de Personal"
        ),
        "Kâr / Zarar Raporu" to mapOf(
            AppLanguage.EN to "Profit & Loss Report",
            AppLanguage.TR to "Kâr / Zarar Raporu",
            AppLanguage.RU to "Отчет о прибылях и убытках",
            AppLanguage.DE to "Gewinn- und Verlustbericht",
            AppLanguage.FR to "Rapport Pertes et Profits",
            AppLanguage.ES to "Informe de Pérdidas y Ganancias"
        ),
        "Cari Hesaplar" to mapOf(
            AppLanguage.EN to "Current Accounts",
            AppLanguage.TR to "Cari Hesaplar",
            AppLanguage.RU to "Текущие счета",
            AppLanguage.DE to "Laufende Konten",
            AppLanguage.FR to "Comptes Courants",
            AppLanguage.ES to "Cuentas Corrientes"
        ),
        "Otel Profili" to mapOf(
            AppLanguage.EN to "Hotel Profile",
            AppLanguage.TR to "Otel Profili",
            AppLanguage.RU to "Профиль отеля",
            AppLanguage.DE to "Hotelprofil",
            AppLanguage.FR to "Profil de l'Hôtel",
            AppLanguage.ES to "Perfil del Hotel"
        ),
        "Oda Yönetimi" to mapOf(
            AppLanguage.EN to "Room Management",
            AppLanguage.TR to "Oda Yönetimi",
            AppLanguage.RU to "Управление номерами",
            AppLanguage.DE to "Zimmerverwaltung",
            AppLanguage.FR to "Gestion des Chambres",
            AppLanguage.ES to "Gestión de Habitaciones"
        ),
        "Menü Yönetimi" to mapOf(
            AppLanguage.EN to "Menu Management",
            AppLanguage.TR to "Menü Yönetimi",
            AppLanguage.RU to "Управление меню",
            AppLanguage.DE to "Menüverwaltung",
            AppLanguage.FR to "Gestion du Menu",
            AppLanguage.ES to "Gestión de Menú"
        ),
        "Dinamik Fiyatlandırma" to mapOf(
            AppLanguage.EN to "Dynamic Pricing",
            AppLanguage.TR to "Dinamik Fiyatlandırma",
            AppLanguage.RU to "Динамическое ценообразование",
            AppLanguage.DE to "Dynamische Preisgestaltung",
            AppLanguage.FR to "Tarification Dynamique",
            AppLanguage.ES to "Precios Dinámicos"
        ),
        "Tedarikçi Yönetimi" to mapOf(
            AppLanguage.EN to "Supplier Management",
            AppLanguage.TR to "Tedarikçi Yönetimi",
            AppLanguage.RU to "Управление поставщиками",
            AppLanguage.DE to "Lieferantenverwaltung",
            AppLanguage.FR to "Gestion des Fournisseurs",
            AppLanguage.ES to "Gestión de Proveedores"
        ),
        "Acenta Yönetimi" to mapOf(
            AppLanguage.EN to "Agency Management",
            AppLanguage.TR to "Acenta Yönetimi",
            AppLanguage.RU to "Управление агентствами",
            AppLanguage.DE to "Agenturverwaltung",
            AppLanguage.FR to "Gestion des Agences",
            AppLanguage.ES to "Gestión de Agencias"
        ),
        "Acenta Sözleşmeleri" to mapOf(
            AppLanguage.EN to "Agency Contracts",
            AppLanguage.TR to "Acenta Sözleşmeleri",
            AppLanguage.RU to "Договоры с агентствами",
            AppLanguage.DE to "Agenturverträge",
            AppLanguage.FR to "Contrats d'Agence",
            AppLanguage.ES to "Contratos de Agencia"
        ),
        "Departman Yönetimi" to mapOf(
            AppLanguage.EN to "Department Management",
            AppLanguage.TR to "Departman Yönetimi",
            AppLanguage.RU to "Управление отделами",
            AppLanguage.DE to "Abteilungsverwaltung",
            AppLanguage.FR to "Gestion des Départements",
            AppLanguage.ES to "Gestión de Departamentos"
        ),
        "Kara Liste" to mapOf(
            AppLanguage.EN to "Blacklist",
            AppLanguage.TR to "Kara Liste",
            AppLanguage.RU to "Черный список",
            AppLanguage.DE to "Schwarze Liste",
            AppLanguage.FR to "Liste Noire",
            AppLanguage.ES to "Lista Negra"
        ),
        "Bakım & Onarım" to mapOf(
            AppLanguage.EN to "Maintenance",
            AppLanguage.TR to "Bakım & Onarım",
            AppLanguage.RU to "Техобслуживание и ремонт",
            AppLanguage.DE to "Wartung & Reparatur",
            AppLanguage.FR to "Maintenance & Réparation",
            AppLanguage.ES to "Mantenimiento y Reparación"
        ),
        "Rezervasyonlar" to mapOf(
            AppLanguage.EN to "Reservations",
            AppLanguage.TR to "Rezervasyonlar",
            AppLanguage.RU to "Бронирования",
            AppLanguage.DE to "Reservierungen",
            AppLanguage.FR to "Réservations",
            AppLanguage.ES to "Reservas"
        ),
        "Çıkış Yapacaklar (Check-Out)" to mapOf(
            AppLanguage.EN to "Check-Out List",
            AppLanguage.TR to "Çıkış Yapacaklar (Check-Out)",
            AppLanguage.RU to "Выезжающие (Check-Out)",
            AppLanguage.DE to "Abreiseliste (Check-Out)",
            AppLanguage.FR to "Départs (Check-Out)",
            AppLanguage.ES to "Salidas (Check-Out)"
        ),
        "Giriş Yapacaklar (Check-In)" to mapOf(
            AppLanguage.EN to "Check-In List",
            AppLanguage.TR to "Giriş Yapacaklar (Check-In)",
            AppLanguage.RU to "Заезжающие (Check-In)",
            AppLanguage.DE to "Anreiseliste (Check-In)",
            AppLanguage.FR to "Arrivées (Check-In)",
            AppLanguage.ES to "Llegadas (Check-In)"
        ),
        "Yeni Rezervasyon" to mapOf(
            AppLanguage.EN to "New Reservation",
            AppLanguage.TR to "Yeni Rezervasyon",
            AppLanguage.RU to "Новое бронирование",
            AppLanguage.DE to "Neue Reservierung",
            AppLanguage.FR to "Nouvelle Réservation",
            AppLanguage.ES to "Nueva Reserva"
        ),
        "Takvim" to mapOf(
            AppLanguage.EN to "Calendar",
            AppLanguage.TR to "Takvim",
            AppLanguage.RU to "Календарь",
            AppLanguage.DE to "Kalender",
            AppLanguage.FR to "Calendrier",
            AppLanguage.ES to "Calendario"
        ),
        "Dil / Language" to mapOf(
            AppLanguage.EN to "Language",
            AppLanguage.TR to "Dil",
            AppLanguage.RU to "Язык",
            AppLanguage.DE to "Sprache",
            AppLanguage.FR to "Langue",
            AppLanguage.ES to "Idioma"
        ),
        "Açık Masalar" to mapOf(
            AppLanguage.EN to "Open Tables",
            AppLanguage.TR to "Açık Masalar",
            AppLanguage.DE to "Offene Tische",
            AppLanguage.FR to "Tables Ouvertes",
            AppLanguage.ES to "Mesas Abiertas",
            AppLanguage.RU to "Открытые Столы"
        ),
        "Masa Aktif" to mapOf(
            AppLanguage.EN to "Active Tables",
            AppLanguage.TR to "Masa Aktif",
            AppLanguage.DE to "Aktive Tische",
            AppLanguage.FR to "Tables Actives",
            AppLanguage.ES to "Mesas Activas",
            AppLanguage.RU to "Активные Столы"
        ),
        "Yeni Masa Aç" to mapOf(
            AppLanguage.EN to "Open New Table",
            AppLanguage.TR to "Yeni Masa Aç",
            AppLanguage.DE to "Neuen Tisch Öffnen",
            AppLanguage.FR to "Ouvrir une Nouvelle Table",
            AppLanguage.ES to "Abrir Nueva Mesa",
            AppLanguage.RU to "Открыть Новый Стол"
        ),
        "Restoran POS" to mapOf(
            AppLanguage.EN to "Restaurant POS",
            AppLanguage.TR to "Restoran POS",
            AppLanguage.DE to "Restaurant POS",
            AppLanguage.FR to "POS Restaurant",
            AppLanguage.ES to "POS Restaurante",
            AppLanguage.RU to "POS Ресторан"
        ),
        "Sipariş Adisyonu" to mapOf(
            AppLanguage.EN to "Order Bill / Check",
            AppLanguage.TR to "Sipariş Adisyonu",
            AppLanguage.DE to "Bestellrechnung",
            AppLanguage.FR to "Addition de Commande",
            AppLanguage.ES to "Cuenta de Pedido",
            AppLanguage.RU to "Счет Заказа"
        ),
        "Manuel Ürün / Hizmet Ekle" to mapOf(
            AppLanguage.EN to "Add Manual Item / Service",
            AppLanguage.TR to "Manuel Ürün / Hizmet Ekle",
            AppLanguage.DE to "Manuelles Produkt Hinzufügen",
            AppLanguage.FR to "Ajouter un Article Manuel",
            AppLanguage.ES to "Añadir Artículo Manual",
            AppLanguage.RU to "Добавить Твар Вручную"
        ),
        "Beklet" to mapOf(
            AppLanguage.EN to "Hold Order",
            AppLanguage.TR to "Beklet",
            AppLanguage.DE to "Halten",
            AppLanguage.FR to "En Mettre en Attente",
            AppLanguage.ES to "En Espera",
            AppLanguage.RU to "Отложить"
        ),
        "Ödeme Al" to mapOf(
            AppLanguage.EN to "Take Payment",
            AppLanguage.TR to "Ödeme Al",
            AppLanguage.DE to "Zahlung Annehmen",
            AppLanguage.FR to "Encaisser",
            AppLanguage.ES to "Cobrar",
            AppLanguage.RU to "Принять Оплату"
        ),
        "Ödeme Yöntemi" to mapOf(
            AppLanguage.EN to "Payment Method",
            AppLanguage.TR to "Ödeme Yöntemi",
            AppLanguage.DE to "Zahlungsmethode",
            AppLanguage.FR to "Mode de Paiement",
            AppLanguage.ES to "Método de Pago",
            AppLanguage.RU to "Способ Оплаты"
        ),
        "Nakit / Kredi Kartı" to mapOf(
            AppLanguage.EN to "Cash / Credit Card",
            AppLanguage.TR to "Nakit / Kredi Kartı",
            AppLanguage.DE to "Bargeld / Kreditkarte",
            AppLanguage.FR to "Espèces / Carte",
            AppLanguage.ES to "Efectivo / Tarjeta",
            AppLanguage.RU to "Наличные / Карта"
        ),
        "Odaya Yaz" to mapOf(
            AppLanguage.EN to "Charge to Room",
            AppLanguage.TR to "Odaya Yaz",
            AppLanguage.DE to "Aufs Zimmer Buchen",
            AppLanguage.FR to "Facturer sur la Chambre",
            AppLanguage.ES to "Cargar a la Habitación",
            AppLanguage.RU to "Записать на Номер"
        ),
        "Oda Seçimi" to mapOf(
            AppLanguage.EN to "Room Selection",
            AppLanguage.TR to "Oda Seçimi",
            AppLanguage.DE to "Zimmerauswahl",
            AppLanguage.FR to "Sélection de Chambre",
            AppLanguage.ES to "Selección de Habitación",
            AppLanguage.RU to "Выбор Номера"
        ),
        "Ödemeyi Tamamla" to mapOf(
            AppLanguage.EN to "Complete Payment",
            AppLanguage.TR to "Ödemeyi Tamamla",
            AppLanguage.DE to "Zahlung Abschließen",
            AppLanguage.FR to "Finaliser le Paiement",
            AppLanguage.ES to "Completar Pago",
            AppLanguage.RU to "Завершить Оплату"
        ),
        "Tümü" to mapOf(
            AppLanguage.EN to "All",
            AppLanguage.TR to "Tümü",
            AppLanguage.DE to "Alle",
            AppLanguage.FR to "Tous",
            AppLanguage.ES to "Todos",
            AppLanguage.RU to "Все"
        ),
        "Yiyecek" to mapOf(
            AppLanguage.EN to "Food",
            AppLanguage.TR to "Yiyecek",
            AppLanguage.DE to "Essen",
            AppLanguage.FR to "Nourriture",
            AppLanguage.ES to "Comida",
            AppLanguage.RU to "Еда"
        ),
        "İçecek" to mapOf(
            AppLanguage.EN to "Beverages",
            AppLanguage.TR to "İçecek",
            AppLanguage.DE to "Getränke",
            AppLanguage.FR to "Boissons",
            AppLanguage.ES to "Bebidas",
            AppLanguage.RU to "Напитки"
        ),
        "Tatlı" to mapOf(
            AppLanguage.EN to "Desserts",
            AppLanguage.TR to "Tatlı",
            AppLanguage.DE to "Nachtisch",
            AppLanguage.FR to "Desserts",
            AppLanguage.ES to "Postres",
            AppLanguage.RU to "Десерты"
        ),
        "Alkol" to mapOf(
            AppLanguage.EN to "Alcohol",
            AppLanguage.TR to "Alkol",
            AppLanguage.DE to "Alkohol",
            AppLanguage.FR to "Alcool",
            AppLanguage.ES to "Alcohol",
            AppLanguage.RU to "Алкоголь"
        ),
        "Diğer" to mapOf(
            AppLanguage.EN to "Other",
            AppLanguage.TR to "Diğer",
            AppLanguage.DE to "Sonstiges",
            AppLanguage.FR to "Autre",
            AppLanguage.ES to "Otros",
            AppLanguage.RU to "Другое"
        ),
        "Ara Toplam" to mapOf(
            AppLanguage.EN to "Subtotal",
            AppLanguage.TR to "Ara Toplam",
            AppLanguage.DE to "Zwischensumme",
            AppLanguage.FR to "Sous-total",
            AppLanguage.ES to "Subtotal",
            AppLanguage.RU to "Промежуточный Итог"
        ),
        "Toplam" to mapOf(
            AppLanguage.EN to "Total",
            AppLanguage.TR to "Toplam",
            AppLanguage.DE to "Gesamtsumme",
            AppLanguage.FR to "Total",
            AppLanguage.ES to "Total",
            AppLanguage.RU to "Итого"
        ),
        "AÇIK" to mapOf(
            AppLanguage.EN to "OPEN",
            AppLanguage.TR to "AÇIK",
            AppLanguage.DE to "OFFEN",
            AppLanguage.FR to "OUVERT",
            AppLanguage.ES to "ABIERTO",
            AppLanguage.RU to "ОТКРЫТ"
        ),
        "Geri" to mapOf(
            AppLanguage.EN to "Back",
            AppLanguage.TR to "Geri",
            AppLanguage.DE to "Zurück",
            AppLanguage.FR to "Retour",
            AppLanguage.ES to "Volver",
            AppLanguage.RU to "Назад"
        ),
        "Kapat" to mapOf(
            AppLanguage.EN to "Close",
            AppLanguage.TR to "Kapat",
            AppLanguage.DE to "Schließen",
            AppLanguage.FR to "Fermer",
            AppLanguage.ES to "Cerrar",
            AppLanguage.RU to "Закрыть"
        ),
        "Kaydet" to mapOf(
            AppLanguage.EN to "Save",
            AppLanguage.TR to "Kaydet",
            AppLanguage.DE to "Speichern",
            AppLanguage.FR to "Enregistrer",
            AppLanguage.ES to "Guardar",
            AppLanguage.RU to "Сохранить"
        ),
        "İptal" to mapOf(
            AppLanguage.EN to "Cancel",
            AppLanguage.TR to "İptal",
            AppLanguage.DE to "Abbrechen",
            AppLanguage.FR to "Annuler",
            AppLanguage.ES to "Cancelar",
            AppLanguage.RU to "Отмена"
        ),
        "Sil" to mapOf(
            AppLanguage.EN to "Delete",
            AppLanguage.TR to "Sil",
            AppLanguage.DE to "Löschen",
            AppLanguage.FR to "Supprimer",
            AppLanguage.ES to "Eliminar",
            AppLanguage.RU to "Удалить"
        ),
        "Gösterge Paneli" to mapOf(
            AppLanguage.EN to "Dashboard",
            AppLanguage.TR to "Gösterge Paneli",
            AppLanguage.DE to "Armaturenbrett",
            AppLanguage.FR to "Tableau de Bord",
            AppLanguage.ES to "Panel de Control",
            AppLanguage.RU to "Панель Управления"
        ),
        "Ön Büro" to mapOf(
            AppLanguage.EN to "Front Desk",
            AppLanguage.TR to "Ön Büro",
            AppLanguage.DE to "Rezeption",
            AppLanguage.FR to "Réception",
            AppLanguage.ES to "Recepción",
            AppLanguage.RU to "Ресепшн"
        ),
        "Personel Yönetimi" to mapOf(
            AppLanguage.EN to "Staff Management",
            AppLanguage.TR to "Personel Yönetimi",
            AppLanguage.DE to "Personalverwaltung",
            AppLanguage.FR to "Gestion du Personnel",
            AppLanguage.ES to "Gestión de Personal",
            AppLanguage.RU to "Управление Персоналом"
        ),
        "Envanter Yönetimi" to mapOf(
            AppLanguage.EN to "Inventory Management",
            AppLanguage.TR to "Envanter Yönetimi",
            AppLanguage.DE to "Bestandsverwaltung",
            AppLanguage.FR to "Gestion des Stocks",
            AppLanguage.ES to "Gestión de Inventario",
            AppLanguage.RU to "Управление Запасами"
        ),
        "Restoran & Oda Servisi Menü Yönetimi" to mapOf(
            AppLanguage.EN to "Restaurant & Room Service Menu Management",
            AppLanguage.TR to "Restoran & Oda Servisi Menü Yönetimi",
            AppLanguage.DE to "Restaurant & Zimmerservice Menüverwaltung",
            AppLanguage.FR to "Gestion du Menu Restaurant & Service en Chambre",
            AppLanguage.ES to "Gestión de Menú de Restaurante y Servicio de Habitaciones",
            AppLanguage.RU to "Управление Меню Ресторана и Обслуживания Номеров"
        ),
        "Otel Profili" to mapOf(
            AppLanguage.EN to "Hotel Profile",
            AppLanguage.TR to "Otel Profili",
            AppLanguage.DE to "Hotelprofil",
            AppLanguage.FR to "Profil de l'Hôtel",
            AppLanguage.ES to "Perfil del Hotel",
            AppLanguage.RU to "Профиль Отеля"
        ),
        "Oda Yönetimi" to mapOf(
            AppLanguage.EN to "Room Management",
            AppLanguage.TR to "Oda Yönetimi",
            AppLanguage.DE to "Zimmerverwaltung",
            AppLanguage.FR to "Gestion des Chambres",
            AppLanguage.ES to "Gestión de Habitaciones",
            AppLanguage.RU to "Управление Номерами"
        ),
        "Fiyat Periyotları" to mapOf(
            AppLanguage.EN to "Pricing Periods",
            AppLanguage.TR to "Fiyat Periyotları",
            AppLanguage.DE to "Preisperioden",
            AppLanguage.FR to "Périodes de Tarification",
            AppLanguage.ES to "Períodos de Precios",
            AppLanguage.RU to "Периоды Цен"
        ),
        "Tedarikçi Yönetimi" to mapOf(
            AppLanguage.EN to "Supplier Management",
            AppLanguage.TR to "Tedarikçi Yönetimi",
            AppLanguage.DE to "Lieferantenverwaltung",
            AppLanguage.FR to "Gestion des Fournisseurs",
            AppLanguage.ES to "Gestión de Proveedores",
            AppLanguage.RU to "Управление Поставщиками"
        ),
        "Acenta Yönetimi" to mapOf(
            AppLanguage.EN to "Agency Management",
            AppLanguage.TR to "Acenta Yönetimi",
            AppLanguage.DE to "Agenturverwaltung",
            AppLanguage.FR to "Gestion des Agences",
            AppLanguage.ES to "Gestión de Agencias",
            AppLanguage.RU to "Управление Агентствами"
        ),
        "Acente Kontenjan Sözleşmesi" to mapOf(
            AppLanguage.EN to "Agency Allotment Contract",
            AppLanguage.TR to "Acente Kontenjan Sözleşmesi",
            AppLanguage.DE to "Agentur Kontingentvertrag",
            AppLanguage.FR to "Contrat de Contingent d'Agence",
            AppLanguage.ES to "Contrato de Cupo de Agencia",
            AppLanguage.RU to "Контракт Квоты Агентства"
        ),
        "Departman Yönetimi" to mapOf(
            AppLanguage.EN to "Department Management",
            AppLanguage.TR to "Departman Yönetimi",
            AppLanguage.DE to "Abteilungsverwaltung",
            AppLanguage.FR to "Gestion des Départements",
            AppLanguage.ES to "Gestión de Departamentos",
            AppLanguage.RU to "Управление Отделами"
        ),
        "Müşteri Kara Liste Yönetimi" to mapOf(
            AppLanguage.EN to "Customer Blacklist Management",
            AppLanguage.TR to "Müşteri Kara Liste Yönetimi",
            AppLanguage.DE to "Kunden-Blacklist-Verwaltung",
            AppLanguage.FR to "Gestion de la Liste Noire des Clients",
            AppLanguage.ES to "Gestión de Lista Negra de Clientes",
            AppLanguage.RU to "Управление Черным Списком Клиентов"
        ),
        "Güvenli" to mapOf(
            AppLanguage.EN to "Secure",
            AppLanguage.TR to "Güvenli",
            AppLanguage.RU to "Безопасно"
        ),
        "7/24 Erişim" to mapOf(
            AppLanguage.EN to "24/7 Access",
            AppLanguage.TR to "7/24 Erişim",
            AppLanguage.RU to "Доступ 24/7"
        ),
        "Raporlama" to mapOf(
            AppLanguage.EN to "Analytics",
            AppLanguage.TR to "Raporlama",
            AppLanguage.RU to "Аналитика"
        ),
        "Hoş Geldiniz" to mapOf(
            AppLanguage.EN to "Welcome",
            AppLanguage.TR to "Hoş Geldiniz",
            AppLanguage.RU to "Добро пожаловать"
        ),
        "Otel Yönetim Sistemine Giriş Yapın" to mapOf(
            AppLanguage.EN to "Sign in to Hotel Management System",
            AppLanguage.TR to "Otel Yönetim Sistemine Giriş Yapın",
            AppLanguage.RU to "Вход в систему управления отелем"
        ),
        "E-posta adresiniz" to mapOf(
            AppLanguage.EN to "Email address",
            AppLanguage.TR to "E-posta adresiniz",
            AppLanguage.RU to "Электронная почта"
        ),
        "Şifreniz" to mapOf(
            AppLanguage.EN to "Password",
            AppLanguage.TR to "Şifreniz",
            AppLanguage.RU to "Пароль"
        ),
        "Üye Kodu (Örn: MBR-A8X92)" to mapOf(
            AppLanguage.EN to "Member Code (e.g. MBR-A8X92)",
            AppLanguage.TR to "Üye Kodu (Örn: MBR-A8X92)",
            AppLanguage.RU to "Код участника (напр. MBR-A8X92)"
        ),
        "Admin tarafından tanımlanan benzersiz üyelik kodu" to mapOf(
            AppLanguage.EN to "Unique membership code assigned by administrator",
            AppLanguage.TR to "Admin tarafından tanımlanan benzersiz üyelik kodu",
            AppLanguage.RU to "Код участника, присвоенный администратором"
        ),
        "Beni hatırla" to mapOf(
            AppLanguage.EN to "Remember me",
            AppLanguage.TR to "Beni hatırla",
            AppLanguage.RU to "Запомнить меня"
        ),
        "Şifremi Unuttum?" to mapOf(
            AppLanguage.EN to "Forgot Password?",
            AppLanguage.TR to "Şifremi Unuttum?",
            AppLanguage.RU to "Забыли пароль?"
        ),
        "Giriş Yap" to mapOf(
            AppLanguage.EN to "Sign In",
            AppLanguage.TR to "Giriş Yap",
            AppLanguage.RU to "Войти"
        ),
        "Hesabınız yok mu?" to mapOf(
            AppLanguage.EN to "Don't have an account?",
            AppLanguage.TR to "Hesabınız yok mu?",
            AppLanguage.RU to "Нет аккаунта?"
        ),
        "Yeni Kayıt" to mapOf(
            AppLanguage.EN to "Register",
            AppLanguage.TR to "Yeni Kayıt",
            AppLanguage.RU to "Регистрация"
        ),
        "Регистрация" to mapOf(
            AppLanguage.EN to "Register",
            AppLanguage.TR to "Kayıt Ol",
            AppLanguage.RU to "Регистрация"
        )
    )

    fun translate(text: String, lang: AppLanguage = _currentLanguage.value): String {
        val translations = translationDictionary[text] ?: return text
        
        // If target language is TR and dictionary has it
        if (lang == AppLanguage.TR) {
            return translations[AppLanguage.TR] ?: text
        }
        
        // Return exact language match (EN, DE, FR, ES, RU, etc.)
        return translations[lang] ?: translations[AppLanguage.EN] ?: text
    }
}

/**
 * Dynamic composable helper to localize any string live in Compose UI
 */
@Composable
fun l10n(turkishOrOriginalText: String): String {
    val currentLang by AppLocalizationManager.currentLanguage.collectAsState()
    return AppLocalizationManager.translate(turkishOrOriginalText, currentLang)
}
